<template>
    <div id="notes">
        <div id="visitor-notes" v-if="!!notes.length">
            <div v-for="(note, index) of notes" :key="index" class="item">
                <div class="name-date">
                    <div class="agent-name">{{ note.createdUser.name }}</div>
                    <div
                        class="date"
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                        }"
                        :content="$moment(note.createdAt).format('LLL')"
                    >
                        {{ $moment(note.createdAt).fromNow() }}
                    </div>
                </div>
                <div class="agent-note">{{ note.description }}</div>
            </div>
        </div>
        <div class="empty-notes-container" v-else>
            <div class="empty-notes-content">
                <div class="empty-notes-icon">
                    <span class="material-icons">note_alt</span>
                </div>
                <div class="empty-notes-message">{{ $t("visitor.notUserData") }}</div>
                <div class="empty-notes-description">Bu kişiye ait henüz not eklenmemiş.</div>
            </div>
        </div>
        <div v-if="isModalVisible" class="custom-visitor-notes-modal-overlay" @click="handleCancel">
            <div class="custom-visitor-notes-modal" @click.stop>
                <div class="custom-visitor-notes-modal-header">
                    <h3>{{ $t('message.addNote') }}</h3>
                    <button class="custom-visitor-notes-modal-close-button" @click="handleCancel">
                        <span class="material-icons">close</span>
                    </button>
                </div>
                <div class="custom-visitor-notes-modal-body">
                    <div class="note-form">
                        <textarea
                            v-model="description"
                            :placeholder="$t('message.addUserNote')"
                            class="non-resizable"
                        ></textarea>
                        <div class="note-form-actions" style="display: none">
                            <a-checkbox v-model="isPrivate">{{ $t("message.privateNote") }}</a-checkbox>
                        </div>
                    </div>
                </div>
                <div class="custom-visitor-notes-modal-footer">
                    <button class="custom-visitor-notes-modal-cancel-button" @click="handleCancel">
                        {{ $t('message.cancel') }}
                    </button>
                    <button class="custom-visitor-notes-modal-ok-button" @click="handleOk">
                        {{ $t('message.send') }}
                    </button>
                </div>
            </div>
        </div>
        <a-tooltip placement="topLeft">
            <template slot="title">
                <span>Not Oluştur</span>
            </template>
            <button class="add-attribute-button" @click="showModal">
                <span class="material-icons">add</span>
            </button>
        </a-tooltip>
    </div>
</template>

<script>
import { log2 } from "../../../utils/log2";
import { mapActions, mapState } from "vuex";
import { sendSocketMessage } from "../../../utils/socketStream";

const log = log2("VisitorNotes");

export default {
    name: "VisitorNotes",
    components: {},
    props: {
        notes: {
            type: Array,
            default: () => [],
        },
        visitorId: {
            type: String,
            required: true,
        },
    },
    data: () => ({
        isModalVisible: false,
        isPrivate: false,
        description: null,
    }),
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
        }),
    },
    methods: {
        ...mapActions({
            createNote: "visitor/createVisitorNote",
            updateContactFields: "contact/updateContactFields",
        }),
        showModal() {
            this.isModalVisible = true;
        },
        handleCancel() {
            this.isModalVisible = false;
            this.resetForm();
        },
        handleOk() {
            this.submitForm();
        },
        resetForm() {
            this.description = null;
            this.isPrivate = false;
        },
        submitForm() {
            this.createNote({
                visitorId: this.visitorId,
                payload: {
                    isPrivate: this.isPrivate,
                    description: this.description,
                },
            }).then(() => {
                if (!this.isPrivate) {
                    sendSocketMessage("visitor note-added", {
                        siteId: this.site._id,
                        contactId: this.visitorId,
                    });
                } else {
                    this.updateContactFields({
                        contactId: this.visitorId,
                        set: {
                            noteCount: this.notes.length + 1,
                        },
                    });
                }

                this.$emit("note-added");
                this.isModalVisible = false;
                this.resetForm();
            });
        },
    },
};
</script>

<style lang="scss">
#notes {
    height: 100%;
    display: flex;
    flex-direction: column;

    #visitor-notes {
        color: var(--color7) !important;
        display: flex;
        flex-direction: column;
        top: 90px;
        bottom: 30px;
        width: 100%;
        height: 100%;
        max-height: 386px;
        overflow-y: auto;
        border: 1px solid #dbe1e8;
        border-radius: 8px;
        background-color: #fff;

        .name-date {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .last-session-data {
            max-width: 200px;
            color: #516173;
            font-weight: 500;
            font-size: 14px;
            line-height: 20px;
        }

        .item {
            border-bottom: 1px solid #dbe1e8;
            width: 100%;
            padding: 18px 25px;

            &:last-child {
                border-bottom: none;
            }

            .agent-name {
                font-weight: 600;
                font-size: 15px;
                color: #374151;
            }

            .date {
                font-size: 12px;
                color: #6b7280;
            }

            .agent-note {
                margin-top: 5px;
                font-size: 14px;
                color: #4b5563;
                line-height: 1.5;
                white-space: pre-line;
            }
        }
    }

    .empty-notes-container {
        flex: 1;
        width: 100%;
        height: 100%;
        border: 1px solid #dbe1e8;
        border-radius: 8px;
        background-color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 30px;

        .empty-notes-content {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            max-width: 300px;

            .empty-notes-icon {
                width: 64px;
                height: 64px;
                border-radius: 50%;
                background-color: rgba(99, 102, 241, 0.1);
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 16px;

                .material-icons {
                    font-size: 32px;
                    color: #6366f1;
                }
            }

            .empty-notes-message {
                font-weight: 600;
                font-size: 16px;
                color: #374151;
                margin-bottom: 8px;
            }

            .empty-notes-description {
                font-size: 14px;
                color: #6b7280;
                line-height: 1.5;
            }
        }
    }

    .secondary-btn {
        background-color: transparent !important;
        border: 1px solid #6366f1 !important;
        color: #6366f1 !important;
        font-size: 12px !important;
        padding: 6px 12px !important;
        border-radius: 6px !important;
        display: flex !important;
        align-items: center !important;
        cursor: pointer !important;
        gap: 6px !important;

        .material-icons {
            background: none !important;
            width: auto !important;
            height: auto !important;
            min-width: auto !important;
            font-size: 16px !important;
            color: #6366f1 !important;
            margin-right: 4px !important;
        }

        &:hover {
            background-color: rgba(99, 102, 241, 0.05) !important;
        }
    }
}

// Dark mode desteği
.dark-mode {
    #notes {
        #visitor-notes {
            border-color: #374151;
            background-color: #1f2937;

            .item {
                border-color: #374151;

                .agent-name {
                    color: #e5e7eb;
                }

                .date {
                    color: #9ca3af;
                }

                .agent-note {
                    color: #d1d5db;
                }
            }
        }

        .empty-notes-container {
            border-color: #374151;
            background-color: #1f2937;

            .empty-notes-content {
                .empty-notes-icon {
                    background-color: rgba(129, 140, 248, 0.15);

                    .material-icons {
                        color: #818cf8;
                    }
                }

                .empty-notes-message {
                    color: #e5e7eb;
                }

                .empty-notes-description {
                    color: #9ca3af;
                }
            }
        }
    }
}

.note-form {
    display: flex;
    flex-direction: column;
    gap: 12px;

    textarea {
        width: 100%;
        padding: 8px;
        border: 1px solid #dbe1e8;
        border-radius: 4px;
        resize: none;
        min-height: 100px;
        background-color: #fff;

        &:focus {
            outline: none;
            border-color: #6366f1;
        }
    }

    &-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
}

.non-resizable {
    resize: none !important;
}

.add-attribute-button {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #6366f1;
    border: none;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
        background-color: #4f46e5;
    }

    .material-icons {
        font-size: 24px;
    }
}

.custom-visitor-notes-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1055;
}

.custom-visitor-notes-modal {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow: hidden;
    animation: modalFadeIn 0.3s ease-out;

    .custom-visitor-notes-modal-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h3 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #374151;
        }

        .custom-visitor-notes-modal-close-button {
            background: none;
            border: none;
            padding: 4px;
            border-radius: 4px;
            cursor: pointer;
            color: #6b7280;
            transition: all 0.2s ease;

            &:hover {
                background-color: #f3f4f6;
                color: #374151;
            }

            .material-icons {
                font-size: 20px;
            }
        }
    }

    .custom-visitor-notes-modal-body {
        padding: 24px;
        max-height: 60vh;
        overflow-y: auto;
    }

    .custom-visitor-notes-modal-footer {
        padding: 16px 24px;
        border-top: 1px solid #e5e7eb;
        display: flex;
        justify-content: flex-end;
        gap: 12px;

        .custom-visitor-notes-modal-cancel-button {
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: 1px solid #d1d5db;
            background-color: #f9fafb;
            color: #374151;

            &:hover {
                background-color: #f3f4f6;
            }
        }

        .custom-visitor-notes-modal-ok-button {
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            background-color: #6366f1;
            color: #fff;

            &:hover {
                background-color: #4f46e5;
            }

            &:disabled {
                background-color: #d1d5db;
                cursor: not-allowed;
            }
        }
    }
}

@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: scale(0.9);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

// Dark mode desteği için ek stiller
.dark-mode {
    .custom-visitor-notes-modal {
        background-color: #1f2937;

        .custom-visitor-notes-modal-header {
            border-color: #374151;

            h3 {
                color: #e5e7eb;
            }

            .custom-visitor-notes-modal-close-button {
                color: #9ca3af;

                &:hover {
                    background-color: #374151;
                    color: #e5e7eb;
                }
            }
        }

        .custom-visitor-notes-modal-footer {
            border-color: #374151;

            .custom-visitor-notes-modal-cancel-button {
                background-color: #374151;
                color: #e5e7eb;
                border-color: #4b5563;

                &:hover {
                    background-color: #4b5563;
                }
            }

            .custom-visitor-notes-modal-ok-button {
                background-color: #818cf8;

                &:hover {
                    background-color: #6366f1;
                }
            }
        }
    }

    .note-form {
        textarea {
            background-color: #1f2937;
            border-color: #374151;
            color: #e5e7eb;

            &:focus {
                border-color: #818cf8;
            }
        }
    }
}
</style>
